import { ChecklistModel, createChecklistItem } from '@akin-home/checklists';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { useState } from 'react';
import {
  GestureResponderEvent,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import styled from 'styled-components/native';
import { CheckListItem } from '../../models';
import { color, palette, spacing } from '../../theme';
import { Button } from '../Button/Button';
import { textPresets } from '../Button/Button.presets';
import { Checkbox } from '../Checkbox/Checkbox';
import { ProfileIcons } from '../ProfileIcons/ProfileIcons';
import { Text } from '../Text/Text';
import { TextListInput } from '../TextListInput/TextListInput';
import {
  ADD_TEXT_AREA,
  CONTENT_VIEW,
  EDIT_ICON,
  FAMILY_MEMBERS_ICONS,
  ICON_VIEW,
  ITEM_CANCEL_BUTTON,
  ITEM_VIEW,
  LIST_EDIT_BUTTON_VIEW,
  MAIN_VIEW,
  TITLE_VIEW,
  viewPresets,
} from './TaskListItemView.presets';

const hitSlop = {
  top: spacing[3],
  bottom: spacing[3],
  left: spacing[8],
  right: spacing[6],
};

export interface TaskListItemViewProps {
  item: ChecklistModel;
  onPress?: (item: ChecklistModel, event: GestureResponderEvent) => void;
  onEditPress?: (item: ChecklistModel, event: GestureResponderEvent) => void;
  onChecklistPress?: (taskItem: ChecklistModel, item: CheckListItem) => void;
  onUpdateListItem?: (
    taskItem: ChecklistModel,
    item: CheckListItem,
    isDelete: boolean
  ) => void;
  /**
   * An optional style override useful for padding & margin.
   */
  style?: ViewStyle | ViewStyle[];
}

export function TaskListItemView(props: TaskListItemViewProps): JSX.Element {
  const { item, style: styleOverride } = props;

  const [isListSelected, setIsListSelected] = useState<boolean>(false);
  const [isAddList, setIsAddList] = useState<boolean>(false);

  const participants = item.participants;

  const checkList = item.checklist;

  const viewStyle = StyleSheet.flatten([
    viewPresets.Event,
    styleOverride || [],
  ]);

  const selectedStyle = {
    ...viewStyle,
    borderColor: palette.Secondary_blue,
    borderWidth: 2,
  };

  const onPressListContainer = (ev: any) => {
    if (props.onPress) {
      props.onPress(item, ev);
    } else if (props.onUpdateListItem) {
      setIsListSelected(!isListSelected);
      setIsAddList(false);
    }
  };

  const addNewListItem = (value: any) => {
    props.onUpdateListItem?.(item, value[0], false);
  };

  const content = checkList && (
    <View style={CONTENT_VIEW}>
      {checkList.map((checklistItem: CheckListItem) => (
        <View key={checklistItem.id} style={ITEM_VIEW}>
          <Checkbox
            key={checklistItem.id}
            preset={
              item.type === 'Shopping'
                ? 'circle'
                : item.type === 'ToDo'
                ? 'toDoCheck'
                : 'checkList'
            }
            label={checklistItem.title}
            value={checklistItem.checked}
            onChange={(newVal) => {
              props.onChecklistPress &&
                props.onChecklistPress(item, {
                  ...checklistItem,
                  checked: newVal,
                });
            }}
          />

          {isListSelected ? (
            <Pressable
              style={ITEM_CANCEL_BUTTON}
              onPress={() =>
                props.onUpdateListItem &&
                props.onUpdateListItem(item, checklistItem, true)
              }
              hitSlop={hitSlop}
            >
              <FontAwesomeIcon
                style={{}}
                size={18}
                icon={['fas', 'times']}
                color={palette.Primary_blue}
              />
            </Pressable>
          ) : null}
        </View>
      ))}
    </View>
  );

  return (
    <ListContainer style={isListSelected ? selectedStyle : viewStyle}>
      <TouchableOpacity style={MAIN_VIEW} onPress={onPressListContainer}>
        {item.primaryShoppingList ? (
          <PrimaryListStar>
            <FontAwesomeIcon icon="star" color={palette.primary_700} />
          </PrimaryListStar>
        ) : null}

        <View style={ICON_VIEW}>
          {participants && participants.length ? (
            <View style={FAMILY_MEMBERS_ICONS}>
              <ProfileIcons
                profiles={participants}
                preset="overlapRight"
                displayCount={2}
              />
            </View>
          ) : null}
        </View>
        <View style={TITLE_VIEW}>
          <Text preset="bold">{item.title}</Text>
        </View>
        {props.onEditPress ? (
          <Button
            preset="text"
            onPress={(event) =>
              props.onEditPress && props.onEditPress(item, event)
            }
          >
            <FontAwesomeIcon style={EDIT_ICON} icon="ellipsis-vertical" />
          </Button>
        ) : null}
      </TouchableOpacity>
      {content}
      {isListSelected ? (
        <View style={LIST_EDIT_BUTTON_VIEW}>
          <Button
            preset="secondary"
            text="Add to list"
            onPress={() => setIsAddList(!isAddList)}
          >
            <FontAwesomeIcon color={color.primary} icon={['fas', 'plus']} />
            <Text style={textPresets.tertiary}> Add to list</Text>
          </Button>
        </View>
      ) : null}
      {isAddList && isListSelected ? (
        <View style={ADD_TEXT_AREA}>
          <TextListInput
            placeholder={'Add item'}
            onChange={addNewListItem}
            transformTextToItem={(text: string): CheckListItem =>
              createChecklistItem({ title: text })
            }
            getItemText={(item: CheckListItem): string => item.title}
          />
        </View>
      ) : null}
    </ListContainer>
  );
}

const PrimaryListStar = styled.View`
  height: 25px;
  width: 25px;

  align-items: center;
  justify-content: center;

  background-color: ${palette.primary_200};

  position: absolute;

  top: 0;
  left: 0;

  border-radius: 20px;
`;

const ListContainer = styled.View``;
